import { type HealthStatus } from "@/infra/observability/performance/healthPolicies";
import { careEnvironment } from "@akina/config";
import {
  type AmplitudeProperties,
  type PropertyTypeMap,
  type SessionPropertyTypeMap,
  configureAnalyticsService,
} from "@akina/infra";

export enum OnboardingAmplitudeProperties {
  EventCause = "event_cause",
  ScreenDurationAtExit = "screen_duration_at_exit",
}

export const CANCEL_REASON_KEYS = ["THERAPY_GOAL_REACHED", "PROGRAM_NOT_A_FIT", "PRICE_TOO_HIGH", "OTHER"] as const;
export type CancelReasonKey = (typeof CANCEL_REASON_KEYS)[number];

export enum CustomProperties {
  SelectionEnumList = "selection_enum_list",
  SelectionEnum = "selection_enum",
  MediaUploadFormatList = "media_upload_format_list",
  MediaUploadSuccess = "media_upload_success",
  TreatmentTemplateKey = "treatment_template_key",
  SelectionBoolean = "selection_boolean",
  SelectionLikert = "selection_likert",
  FeedbackPresent = "feedback_present",
  EventCause = "event_cause",
  AccessedFrom = "accessed_from",
  SoftwareVersion = "software_version",
  CancelReason = "cancel_reason",
  PatientId = "patient_id",
}
export type EventCauseType = "submit" | "tab_close_exit" | "register" | "complete" | "manual_exit";

type OnboardingPropertyTypeMap = {
  [OnboardingAmplitudeProperties.EventCause]: EventCauseType;
  [OnboardingAmplitudeProperties.ScreenDurationAtExit]: number | null;
};

export type CheckBoxType = "TC" | "PP" | "SD";
export type AppOrWeb = "app" | "web";

type AppOrWebPropertyTypeMap = Omit<
  PropertyTypeMap,
  | "battery_level"
  | "battery_charging"
  | "battery_available"
  | "battery_health"
  | "gpu_integrated"
  | "gpu_discrete"
  | "gpu_cpu"
  | "gpu_health"
>;

export type CustomPropertyTypeMap = {
  [CustomProperties.SelectionEnumList]?: CheckBoxType[] | string[];
  [CustomProperties.MediaUploadFormatList]?: string[];
  [CustomProperties.MediaUploadSuccess]?: boolean;
  [CustomProperties.TreatmentTemplateKey]?: string;
  [CustomProperties.SelectionBoolean]?: boolean;
  [CustomProperties.SelectionLikert]?: number;
  [CustomProperties.SelectionEnum]?: string;
  [CustomProperties.FeedbackPresent]?: boolean;
  [CustomProperties.EventCause]?: EventCauseType;
  [CustomProperties.AccessedFrom]?: AppOrWeb;
  [CustomProperties.SoftwareVersion]?: string;
};

export enum EventType {
  ButtonClick = "Button Click",
  TimeSpentOnPage = "Time Spent On Page",
  OnboardingPageDropped = "Onboarding Page Dropped",
  TimeSpentInOnboarding = "Time Spent In Onboarding",
  SessionStartButton = "Session Start Button",
  StartOnboardingExit = "Start Onboarding Exit",
  OnboardingTermsConditionsExit = "Onboarding Terms Conditions Selection Exit",
  CareSupportContactAccess = "Care Support Contact Access",
  CareSettingsPageExit = "Care Settings Page Exit",
  TermsAndConditionAccess = "Terms and Condition Access",
  InstructionForUseAccess = "Instruction For Use Access",
  DataTransparencyAccess = "Data Transparency Access",
  SelfReflectionStart = "Self-Reflection Start",
  SelfReflectionExit = "Self-Reflection Exit",
  SessionOverviewExit = "Session Overview Exit",
  OnboardingCompletedExit = "Onboarding Completed Exit",
  SessionStatisticsViewed = "Session Statistics Viewed",
  PatientAnimationOpened = "Patient Animation Opened",
  AppOrWeb = "App Or Web",
  AppClosed = "App Closed",
  SessionHealthChanged = "Session Health Changed",
  PerformanceMetricsPolling = "Performance Metrics Polling",
  SubscriptionCancelReason = "Subscription Cancel Reason",
}

type PerformancePollingProperties = {
  [AmplitudeProperties.OperatingSystemInformation]: string;
  [AmplitudeProperties.DeviceInformation]: string;
  [AmplitudeProperties.Fps]?: number;
  [AmplitudeProperties.AvailableMemory]?: number | null;
  [AmplitudeProperties.PoseTrackingFrequency]?: number;
  [AmplitudeProperties.NetworkBandwidthMbps]?: number;
  [AmplitudeProperties.NetworkBandwidthQuality]?: string;
  [AmplitudeProperties.MaiaLatencyMs]?: number;
  [AmplitudeProperties.BatteryLevel]?: number;
  [AmplitudeProperties.BatteryCharging]?: boolean;
  [AmplitudeProperties.BatteryAvailable]?: boolean | "unknown";
  [AmplitudeProperties.BatteryHealth]?: string;
  [AmplitudeProperties.GpuIntegrated]?: string;
  [AmplitudeProperties.GpuDiscrete]?: string;
  [AmplitudeProperties.GpuCpu]?: string;
  [AmplitudeProperties.GpuAvailable]?: boolean | "unknown";
  [AmplitudeProperties.GpuHealth]?: string;
};

type SessionHealthProperties = PerformancePollingProperties & {
  [AmplitudeProperties.FpsHealth]: HealthStatus;
  [AmplitudeProperties.MemoryHealth]: HealthStatus;
  [AmplitudeProperties.PoseTrackingHealth]: HealthStatus;
  [AmplitudeProperties.NetworkBandwidthHealth]: HealthStatus;
  [AmplitudeProperties.MaiaLatencyHealth]: HealthStatus;
  [AmplitudeProperties.BatteryHealth]: HealthStatus;
};

type AnalyticsEventMap = {
  [EventType.SessionStatisticsViewed]: PropertyTypeMap & {
    treatment_id: string;
    session_id: string;
  };
  [EventType.PatientAnimationOpened]: PropertyTypeMap & {
    routine_log_id: string;
    session_log_id: string;
  };
  [EventType.SessionStartButton]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.StartOnboardingExit]: PropertyTypeMap & OnboardingPropertyTypeMap;
  [EventType.OnboardingTermsConditionsExit]: PropertyTypeMap &
    OnboardingPropertyTypeMap & {
      [CustomProperties.SelectionEnumList]: CheckBoxType[];
    };
  [EventType.CareSupportContactAccess]: PropertyTypeMap;
  [EventType.CareSettingsPageExit]: PropertyTypeMap & {
    [CustomProperties.SelectionEnumList]?: string[];
  };
  [EventType.AppOrWeb]: AppOrWebPropertyTypeMap & {
    [CustomProperties.AccessedFrom]?: AppOrWeb;
    [CustomProperties.SoftwareVersion]?: string;
  };
  [EventType.TermsAndConditionAccess]: PropertyTypeMap;
  [EventType.InstructionForUseAccess]: PropertyTypeMap;
  [EventType.DataTransparencyAccess]: PropertyTypeMap;
  [EventType.SelfReflectionStart]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.SelfReflectionExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.SessionOverviewExit]: PropertyTypeMap & SessionPropertyTypeMap;
  [EventType.OnboardingCompletedExit]: PropertyTypeMap & OnboardingPropertyTypeMap;
  [EventType.AppClosed]: PropertyTypeMap;
  [EventType.SubscriptionCancelReason]: PropertyTypeMap & {
    [CustomProperties.CancelReason]: CancelReasonKey;
    [CustomProperties.PatientId]: string;
  };
  [EventType.SessionHealthChanged]: SessionHealthProperties;
  [EventType.PerformanceMetricsPolling]: PerformancePollingProperties;
  //Old events
  [EventType.ButtonClick]: { buttonName: string };
  [EventType.TimeSpentOnPage]: { pageName: string; seconds: number };
  [EventType.OnboardingPageDropped]: { pageName: string };
  [EventType.TimeSpentInOnboarding]: { seconds: number };
};

export enum PageNames {
  Today = "Today",
}

export enum ScreenTypes {
  Dashboard = "Dashboard",
  SelfReflection = "Self Reflection",
}

export const analyticsService = configureAnalyticsService(false, {
  amplitudeApiKey: careEnvironment.amplitudeApiKey,
  app: "akina-care",
});

type AnalyticsEventType = keyof AnalyticsEventMap;

const trackEvent = <T extends AnalyticsEventType>(eventName: T, eventData: AnalyticsEventMap[T]) => {
  analyticsService.track(eventName, eventData);
};

const trackEventAsync = <T extends AnalyticsEventType>(eventName: T, eventData: AnalyticsEventMap[T]) =>
  analyticsService.trackAsync(eventName, eventData);

export const trackSessionStartButtonClick = (analyticsData: PropertyTypeMap & SessionPropertyTypeMap) => {
  trackEvent(EventType.SessionStartButton, analyticsData);
};

export const trackCareSupportContactAccess = (analyticsData: PropertyTypeMap) => {
  trackEvent(EventType.CareSupportContactAccess, analyticsData);
};
export const trackCareSettingsPageExit = (
  analyticsData: PropertyTypeMap & {
    [CustomProperties.SelectionEnumList]?: string[];
  },
) => {
  trackEvent(EventType.CareSettingsPageExit, analyticsData);
};

export const trackAppOrWeb = (
  analyticsData: AppOrWebPropertyTypeMap & {
    [CustomProperties.AccessedFrom]?: AppOrWeb;
    [CustomProperties.SoftwareVersion]?: string;
  },
) => {
  trackEvent(EventType.AppOrWeb, analyticsData);
};

export const trackAppClosed = async (analyticsData: PropertyTypeMap) => {
  return trackEventAsync(EventType.AppClosed, analyticsData);
};

export const trackTermsAndConditionsAccess = (analyticsData: PropertyTypeMap) => {
  trackEvent(EventType.TermsAndConditionAccess, analyticsData);
};

export const trackInstructionForUseAccess = (analyticsData: PropertyTypeMap) => {
  trackEvent(EventType.InstructionForUseAccess, analyticsData);
};

export const trackDataTransparencyAccess = (analyticsData: PropertyTypeMap) => {
  trackEvent(EventType.DataTransparencyAccess, analyticsData);
};

export const trackOnboardingPageExit = (
  eventName: EventType,
  analyticsData: PropertyTypeMap & OnboardingPropertyTypeMap & CustomPropertyTypeMap,
) => {
  trackEvent(eventName, analyticsData);
};

export const trackOnboardingPageExitAsync = (
  eventName: EventType,
  analyticsData: PropertyTypeMap & OnboardingPropertyTypeMap & CustomPropertyTypeMap,
) => trackEventAsync(eventName, analyticsData);

export const trackSelfReflectionStart = (analyticsData: PropertyTypeMap & SessionPropertyTypeMap) => {
  trackEvent(EventType.SelfReflectionStart, analyticsData);
};

export const trackSelfReflectionExit = (
  analyticsData: PropertyTypeMap & SessionPropertyTypeMap & CustomPropertyTypeMap,
) => {
  trackEvent(EventType.SelfReflectionExit, analyticsData);
};

export const trackSessionOverviewExit = (
  analyticsData: PropertyTypeMap & SessionPropertyTypeMap & { [CustomProperties.EventCause]: EventCauseType },
) => {
  trackEvent(EventType.SessionOverviewExit, analyticsData);
};

export const trackSessionStatisticsViewed = (
  analyticsData: PropertyTypeMap & {
    treatment_id: string;
    session_id: string;
  },
) => {
  trackEvent(EventType.SessionStatisticsViewed, analyticsData);
};

export const trackPatientAnimationOpened = (
  analyticsData: PropertyTypeMap & {
    routine_log_id: string;
    session_log_id: string;
  },
) => {
  trackEvent(EventType.PatientAnimationOpened, analyticsData);
};

export const trackSessionHealth = (analyticsData: SessionHealthProperties) => {
  trackEvent(EventType.SessionHealthChanged, analyticsData);
};

export const trackPerformanceMetricsPolling = (analyticsData: PerformancePollingProperties) => {
  trackEvent(EventType.PerformanceMetricsPolling, analyticsData);
};

export const trackSubscriptionCancelReason = (
  analyticsData: PropertyTypeMap & {
    [CustomProperties.CancelReason]: CancelReasonKey;
    [CustomProperties.PatientId]: string;
  },
) => {
  trackEvent(EventType.SubscriptionCancelReason, analyticsData);
};
